import React from 'react';

import {
  Button,
  MenuItem,
  Select,
} from '@mui/material';
import { useDispatch } from 'react-redux';

import CreditCardTokenForm from './CreditCardTokenForm';

import type { PublicApiSettingsJson } from '@/domain/payment/service/entity/publicApiSettingsEntity';

import { uiSlice } from '@/domain/ui/redux';
import { service } from '@/share';

type CardType = {
  id: string,
  cardBrand: string,
  expYear: number | undefined,
  expMonth: number | undefined,
  last4: string,
}

/**
 * プロパティ
 */
export type Props = {
  /** ID */
  id: PublicApiSettingsJson,
  /** 施設ID */
  nurseryId: string,
  /** カードIDをセット */
  setCardId: (key: string | null) => void,
  /** カードID */
  cardId: string | null,
}

const getCardList = async (nurseryId: string) => {
  const data = await service.payment.getCreditCardList(nurseryId);
  if (data.error !== '') {
    return { error: data.error, cards: [] };
  }
  const cards: CardType[] = [];
  for (let i = 0; i < data.cards.length; i += 1) {
    const item = data.cards[i];
    cards.push({
      id: item.id,
      cardBrand: item.cardBrand || '',
      expYear: item.expYear,
      expMonth: item.expMonth,
      last4: item.last4 || '',
    });
  }
  return { error: '', cards };
};

const CreditCardContent: React.FC<Props> = ({
  id,
  nurseryId,
  setCardId,
  cardId,
}) => {
  const dispatch = useDispatch();
  const [isInit, setIsInit] = React.useState(false);
  const [isOpenRegistCreditCard, setIsOpenRegistCreditCard] = React.useState(false);
  const [cardList, setCardList] = React.useState<CardType[] | undefined>(undefined);
  React.useEffect(() => {
    if (cardList === undefined) {
      const execProcess = async () => {
        const { cards, error } = await getCardList(nurseryId);
        if (error === '') {
          setCardList(cards);
          if (cards.length > 0) {
            setCardId(cards[0].id);
          } else {
            setCardId(null);
          }
        } else {
          dispatch(uiSlice.actions.setMessage({
            key: 'error',
            content: error,
          }));
        }
        setIsInit(true);
      };
      execProcess();
    }
  }, [nurseryId, cardList, setCardId, dispatch]);
  if (isInit) {
    return (
      <div style={{ margin: '5px 0' }}>
        <h5>支払いに使用するカード</h5>
        {cardId && cardList && cardList.length > 0 ? (
          <Select
            label="クレジットカード一覧"
            value={cardId}
            fullWidth
            onChange={(e) => setCardId(e.target.value)}
          >
            {cardList.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                {`${v.cardBrand} 下4桁 ${v.last4} (期限:${v.expYear}/${v.expMonth})`}
              </MenuItem>
            ))}
          </Select>
        ) : (
          <p>クレジットカードの登録がありません。登録してください。</p>
        )}
        <p style={{ textAlign: 'right' }}>
          <Button
            variant="contained"
            onClick={() => setIsOpenRegistCreditCard(true)}
            style={{ margin: '5px 0' }}
          >
            クレジットカードの追加
          </Button>
        </p>
        {isOpenRegistCreditCard && (
          <CreditCardTokenForm
            id={id}
            regist={async (token: string) => {
              const res = await service.payment.createCreditCard(nurseryId, token);
              const { cards, error } = await getCardList(nurseryId);
              if (error === '') {
                setCardList(cards);
                const index = cards.findIndex((v) => v.id === res.cardId);
                setCardId(cards[index].id);
              } else {
                dispatch(uiSlice.actions.setMessage({
                  key: 'error',
                  content: error,
                }));
              }
            }}
            close={() => setIsOpenRegistCreditCard(false)}
          />
        )}
      </div>
    );
  }
  return (
    <p>初期化中です。お待ちください...</p>
  );
};

export default CreditCardContent;
